import React, { useEffect, useState } from "react";
import { Phone, MessageCircle, PawPrint, Menu, X } from "lucide-react";
import { CLINIC, waLink, telLink } from "../lib/contact";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { href: "#inicio", label: "Inicio" },
    { href: "#servicios", label: "Servicios" },
    { href: "#contacto", label: "Contacto" },
  ];

  return (
    <header
      data-testid="header-nav"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-[#FFFDF7]/85 backdrop-blur-xl border-b border-[#D1E2D6]/80 shadow-[0_4px_24px_rgba(45,58,48,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#inicio"
          className="flex items-center gap-2 group"
          data-testid="header-logo"
        >
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#F28C8C] to-[#E57575] flex items-center justify-center shadow-sm group-hover:rotate-6 transition-transform duration-300">
            <PawPrint className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-semibold text-[#2D3A30] text-lg sm:text-xl">
              Royal Pets
            </div>
            <div className="text-[10px] sm:text-xs text-[#7B9681] tracking-widest uppercase font-semibold">
              Veterinaria
            </div>
          </div>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="font-body font-semibold text-[#4A5E4F] hover:text-[#F28C8C] transition-colors duration-200 text-sm tracking-wide"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href={telLink}
            data-testid="header-call-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 border-[#D1E2D6] text-[#2D3A30] hover:border-[#84B891] hover:bg-[#E3F0E6] transition-all duration-200 font-semibold text-sm"
          >
            <Phone className="w-4 h-4" />
            Llamar
          </a>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white font-semibold text-sm shadow-[0_6px_20px_rgba(37,211,102,0.35)] hover:shadow-[0_10px_28px_rgba(37,211,102,0.45)] hover:-translate-y-0.5 transition-all duration-300"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="header-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden w-10 h-10 rounded-full flex items-center justify-center bg-white/80 backdrop-blur border border-[#D1E2D6] text-[#2D3A30]"
          aria-label="Menú"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile sheet */}
      {open && (
        <div
          data-testid="header-mobile-menu"
          className="md:hidden bg-[#FFFDF7]/95 backdrop-blur-xl border-t border-[#D1E2D6] px-4 py-6 space-y-4 fade-up"
        >
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                className="font-body font-semibold text-[#2D3A30] py-3 px-3 rounded-xl hover:bg-[#E3F0E6]"
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <a
              href={telLink}
              data-testid="mobile-call-btn"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full border-2 border-[#D1E2D6] text-[#2D3A30] font-semibold text-sm"
            >
              <Phone className="w-4 h-4" />
              Llamar
            </a>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-whatsapp-btn"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#25D366] text-white font-semibold text-sm"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
          </div>
          <div className="text-center text-xs text-[#7B9681] pt-2 font-semibold tracking-widest uppercase">
            {CLINIC.hours} · {CLINIC.city}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
